import { useEffect, useRef, useState, type FormEvent } from 'react';
import { SMTP_REASON } from '../../lib/mail-reasons';
import { apiFetch, isApiError } from '../../lib/api';
import { planLimitMessage } from '../../lib/billing-api';
import { listAccounts, type AccountRow } from '../../lib/accounts-api';
import { useAgentStore } from '../../stores/agent';
import { Avatar } from '../../components/Avatar';
import { PhoneInput, isValidE164 } from '../../components/PhoneInput';
import { useDialogFocus } from '../../lib/dialog-focus';
import {
  Button,
  Badge,
  Skeleton,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Label,
} from '../../components/ui';
import { Eye, EyeOff, Sparkles, KeyRound, Plus, UserPlus, Users } from 'lucide-react';
import styles from './admin.module.css';

interface Agent {
  id: string; email: string; displayName: string;
  role: 'agent' | 'site-admin' | 'account-admin' | 'super-admin';
  chatMax: number; createdAt: string; disabledAt: string | null;
  avatarUrl: string | null;
  phone: string | null;
}

interface SiteOption { id: string; accountId: string; name: string; slug: string }

/** What the server reports back about the welcome mail. */
interface MailOutcome { sent: boolean; reason?: string }

/**
 * Why the invite did not go out, in words an admin can act on. The point of
 * each is to say what to DO — an unexplained "not sent" leaves someone waiting
 * for an email while the admin assumes it arrived.
 */
const INVITE_REASON: Record<string, string> = {
  ...SMTP_REASON,
  'no-public-url': 'PUBLIC_APP_URL is not set on the server, so no sign-in link could be built',
  'not-requested': 'you chose not to send one',
  internal: 'an unexpected error — check the server log',
};

function explainMail(outcome: MailOutcome): string {
  const why = outcome.reason ? INVITE_REASON[outcome.reason] ?? outcome.reason : 'an unknown reason';
  return `Account created, but the invite was NOT emailed: ${why}. Send the sign-in details yourself.`;
}

/**
 * A password the admin does not have to invent.
 *
 * `crypto.getRandomValues`, not Math.random — this is the credential that
 * protects an agent account, and it travels by email. The alphabet omits the
 * glyph pairs that are indistinguishable in most fonts (0/O, 1/l/I), because
 * this password gets read off a screen and retyped.
 */
function generatePassword(length = 16): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (n) => alphabet[n % alphabet.length]).join('');
}

/** Role options an admin may grant — mirrors assignableRoles on the server. */
function RoleOptions({ platform }: { platform: boolean }) {
  return (
    <>
      <option value="agent">agent</option>
      <option value="site-admin">site-admin</option>
      <option value="account-admin">account-admin</option>
      {platform && <option value="super-admin">super-admin</option>}
    </>
  );
}

export function AdminAgents() {
  const me = useAgentStore((s) => s.current);
  const isPlatformAdmin = me?.role === 'super-admin';
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  // '' = the caller's own account. Only the platform operator can pick another.
  const [accountId, setAccountId] = useState('');
  const [list, setList] = useState<Agent[]>([]);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Agent['role']>('agent');
  const [chatMax, setChatMax] = useState(3);
  const [phone, setPhone] = useState('');
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendInvite, setSendInvite] = useState(true);
  const [revealPassword, setRevealPassword] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  function openCreateModal() {
    setError(null);
    if (!password) {
      setPassword(generatePassword());
      setRevealPassword(true);
    }
    setCreateModalOpen(true);
  }

  // Survives the form reset on success, because it is the only place the admin
  // is told the mail did NOT go out.
  const [notice, setNotice] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const { agents } = await apiFetch<{ agents: Agent[] }>('/api/admin/agents');
      setList(agents);
    } catch {
      setError('load-failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);
  useEffect(() => {
    void apiFetch<{ sites: SiteOption[] }>('/api/admin/sites')
      .then((r) => setSites(r.sites))
      .catch(() => { /* sites dropdown just stays empty */ });
  }, []);
  useEffect(() => {
    if (!isPlatformAdmin) return;
    void listAccounts()
      .then(setAccounts)
      .catch(() => { /* picker stays on "My account" */ });
  }, [isPlatformAdmin]);
  // An agent can only be assigned to sites of its own account (the server and
  // a DB trigger both refuse otherwise), so offer only those.
  const targetAccount = accountId || me?.accountId;
  const accountSites = sites.filter((s) => !targetAccount || s.accountId === targetAccount);

  const phoneValid = isValidE164(phone);
  const canCreate = !busy && phoneValid && siteIds.length > 0;

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    const invitee = email;
    try {
      const res = await apiFetch<{ invite?: MailOutcome }>('/api/admin/agents', {
        method: 'POST',
        body: JSON.stringify({
          email, displayName, password, role, chatMax,
          phone: phone.trim() || undefined, siteIds, sendInvite,
          ...(accountId ? { accountId } : {}),
        }),
      });
      const invite = res.invite;
      setNotice(
        !sendInvite
          ? `Account created for ${invitee}. No invite was sent — pass the sign-in details on yourself.`
          : invite?.sent
            ? `Account created. Invite emailed to ${invitee}.`
            : explainMail(invite ?? { sent: false }),
      );
      setEmail(''); setDisplayName(''); setPassword(''); setRole('agent'); setChatMax(3); setPhone(''); setSiteIds([]);
      setRevealPassword(false);
      setCreateModalOpen(false);
      await load();
    } catch (err) {
      const e = err as { body?: { issues?: unknown } };
      setError(planLimitMessage(err) ?? (e.body?.issues ? JSON.stringify(e.body.issues) : 'create-failed'));
    } finally {
      setBusy(false);
    }
  }

  async function toggleDisable(a: Agent) {
    try {
      await apiFetch(`/api/admin/agents/${a.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ disabled: !a.disabledAt }),
      });
    } catch (err) {
      setError(planLimitMessage(err) ?? 'update-failed');
      return;
    }
    await load();
  }

  const [resetAgent, setResetAgent] = useState<Agent | null>(null);
  const [resetPasswordVal, setResetPasswordVal] = useState('');
  const [resetReveal, setResetReveal] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  function openResetPasswordModal(a: Agent) {
    setResetAgent(a);
    setResetPasswordVal(generatePassword());
    setResetReveal(true);
    setResetError(null);
  }

  async function handleConfirmResetPassword(e: FormEvent) {
    e.preventDefault();
    if (!resetAgent) return;
    if (resetPasswordVal.length < 8) {
      setResetError('Password must be at least 8 characters.');
      return;
    }
    setResetBusy(true);
    setResetError(null);
    try {
      await apiFetch(`/api/admin/agents/${resetAgent.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ password: resetPasswordVal }),
      });
      setNotice(`Password successfully reset for ${resetAgent.email}.`);
      setResetAgent(null);
    } catch (err) {
      setResetError(planLimitMessage(err) ?? 'Failed to reset password.');
    } finally {
      setResetBusy(false);
    }
  }

  return (
    <section className={styles.section}>
      {/* Header with Title and Create Action on Right */}
      <div className={styles.headerRow}>
        <div className={styles.headerTitles}>
          <div className={styles.kicker}>
            <Users size={13} />
            <span>Workspace Scope</span>
          </div>
          <h1 className={styles.title}>All agents.</h1>
          <p className={styles.subtitle}>
            Manage support agents, site permissions, and real-time conversation capacities.
          </p>
        </div>
        <Button onClick={openCreateModal} className="gap-2 h-10 px-4">
          <Plus size={16} />
          <span>Create Agent</span>
        </Button>
      </div>

      {notice && (
        <div className="mb-6 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs flex items-center justify-between" role="status">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} className="opacity-70 hover:opacity-100 text-xs font-semibold ml-4">✕</button>
        </div>
      )}

      {error && !createModalOpen && (
        <p className={styles.error} style={{ marginBottom: 16 }} role="alert">{error}</p>
      )}

      {/* Create Agent Modal Dialog */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className={styles.agentModalContent}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <UserPlus size={18} className="text-primary" />
              <span>Create New Agent</span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              Add a new support agent to your workspace. Each agent can handle concurrent chats across assigned site properties.
            </DialogDescription>
          </DialogHeader>

          <form className={styles.modalForm} onSubmit={handleCreate}>
            <div className={styles.modalGrid2}>
              <div className={styles.field}>
                <label htmlFor="agent-name" className={styles.label}>Display Name</label>
                <input
                  id="agent-name"
                  className={styles.input}
                  required
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. Alice Mehta"
                  autoFocus
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="agent-email" className={styles.label}>Email Address</label>
                <input
                  id="agent-email"
                  className={styles.input}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="agent@company.com"
                />
              </div>
            </div>

            <div className={styles.modalGrid2}>
              <div className={styles.field}>
                <div className={styles.fieldHead}>
                  <label htmlFor="agent-pwd" className={styles.label}>Password (min 8)</label>
                  <button
                    type="button"
                    className={styles.generateBtn}
                    onClick={() => {
                      setPassword(generatePassword());
                      setRevealPassword(true);
                    }}
                    title="Generate secure temporary password"
                  >
                    <Sparkles size={12} />
                    <span>Generate</span>
                  </button>
                </div>
                <div className={styles.passwordInputWrap}>
                  <input
                    id="agent-pwd"
                    className={styles.passwordInput}
                    type={revealPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className={styles.passwordToggleBtn}
                    onClick={() => setRevealPassword(!revealPassword)}
                    title={revealPassword ? 'Hide password' : 'Show password'}
                    aria-label={revealPassword ? 'Hide password' : 'Show password'}
                  >
                    {revealPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <span className={styles.hint}>
                  {sendInvite
                    ? 'Emailed to the agent as a temporary password.'
                    : 'Not emailed — pass it on yourself.'}
                </span>
              </div>

              <div className={styles.field}>
                <label htmlFor="agent-role" className={styles.label}>Role</label>
                <select
                  id="agent-role"
                  className={styles.select}
                  value={role}
                  onChange={(e) => setRole(e.target.value as Agent['role'])}
                >
                  <RoleOptions platform={isPlatformAdmin} />
                </select>
                <span className={styles.hint}>
                  Determines permissions within this workspace.
                </span>
              </div>
            </div>

            {isPlatformAdmin ? (
              <div className={styles.modalGrid2}>
                <div className={styles.field}>
                  <label htmlFor="agent-acc" className={styles.label}>Account</label>
                  <select
                    id="agent-acc"
                    className={styles.select}
                    value={accountId}
                    onChange={(e) => {
                      setAccountId(e.target.value);
                      setSiteIds([]);
                    }}
                  >
                    <option value="">My account</option>
                    {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>

                <div className={styles.field}>
                  <label htmlFor="agent-cap" className={styles.label}>Chat Cap</label>
                  <input
                    id="agent-cap"
                    className={styles.input}
                    type="number"
                    min={1}
                    max={10}
                    value={chatMax}
                    onChange={(e) => setChatMax(Number(e.target.value))}
                  />
                </div>
              </div>
            ) : null}

            <div className={isPlatformAdmin ? styles.field : styles.modalGrid2}>
              {!isPlatformAdmin && (
                <div className={styles.field}>
                  <label htmlFor="agent-cap" className={styles.label}>Chat Cap</label>
                  <input
                    id="agent-cap"
                    className={styles.input}
                    type="number"
                    min={1}
                    max={10}
                    value={chatMax}
                    onChange={(e) => setChatMax(Number(e.target.value))}
                  />
                </div>
              )}
              <div className={styles.field}>
                <label className={styles.label}>WhatsApp Phone</label>
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                  selectClassName={styles.select}
                  inputClassName={styles.input}
                />
              </div>
            </div>

            <div className={styles.field} style={{ borderTop: '1px solid var(--inic-rule)', paddingTop: 12 }}>
              <span className={styles.label}>Assign to Site Properties (at least one)</span>
              {accountSites.length === 0 ? (
                <p className={styles.hint}>No sites available in this account.</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {accountSites.map((s) => {
                    const checked = siteIds.includes(s.id);
                    return (
                      <label key={s.id} className={styles.siteCheckItem}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => setSiteIds((prev) => e.target.checked ? [...prev, s.id] : prev.filter((id) => id !== s.id))}
                        />
                        <span style={{ fontWeight: 500, fontSize: 13 }}>{s.name}</span>
                        <span className={styles.mono} style={{ fontSize: 11, color: 'var(--inic-ink-muted)' }}>{s.slug}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {siteIds.length === 0 && (
                <span style={{ color: '#d97706', fontSize: 11, fontWeight: 500, marginTop: 4 }}>
                  Pick at least one site property for this agent.
                </span>
              )}
            </div>

            <label className={styles.fieldCheck}>
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
              />
              <span style={{ fontSize: 13, color: 'var(--inic-ink)', fontWeight: 500 }}>
                Email an invite with the sign-in link and this password
              </span>
            </label>

            {error && <p className={styles.error} role="alert">{error}</p>}

            <div className={styles.modalFooter}>
              <Button type="button" variant="outline" size="sm" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={!canCreate || busy}>
                {busy ? 'Creating…' : 'Create Agent →'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {editing ? (
        <EditAgentModal
          agent={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      ) : null}

      <div className={styles.tableWrap}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agent</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="w-[70px]">Cap</TableHead>
              <TableHead className="w-[90px]">Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-44" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-8" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-28 ml-auto" /></TableCell>
                </TableRow>
              ))
            ) : list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-xs text-muted-foreground">
                  No agents found in this account.
                </TableCell>
              </TableRow>
            ) : (
              list.map((a) => (
                <TableRow key={a.id} className="hover:bg-muted/40 transition-colors">
                  <TableCell>
                    <span className="flex items-center gap-2 font-medium text-xs text-foreground">
                      <Avatar name={a.displayName} avatarUrl={a.avatarUrl} size={26} />
                      <span>{a.displayName}</span>
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{a.email}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{a.phone ?? '—'}</TableCell>
                  <TableCell>
                    <Badge
                      variant={a.role === 'super-admin' ? 'default' : a.role === 'account-admin' ? 'outline' : 'secondary'}
                      className="text-[10px] uppercase font-semibold"
                    >
                      {a.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{a.chatMax}</TableCell>
                  <TableCell>
                    {a.disabledAt ? (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">disabled</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-emerald-500 border-emerald-500/20 bg-emerald-500/10">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 inline-block" />
                        active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button variant="outline" size="sm" className="h-7 text-xs px-2.5" onClick={() => setEditing(a)}>
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => toggleDisable(a)}>
                        {a.disabledAt ? 'Enable' : 'Disable'}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => openResetPasswordModal(a)}>
                        Reset pwd
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Reset Password Modal Dialog */}
      <Dialog open={Boolean(resetAgent)} onOpenChange={(open) => { if (!open) setResetAgent(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound size={18} className="text-primary" />
              <span>Reset Password</span>
            </DialogTitle>
            <DialogDescription>
              Set a new temporary password for <strong>{resetAgent?.displayName}</strong> ({resetAgent?.email}).
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4 pt-2" onSubmit={handleConfirmResetPassword}>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="reset-agent-pwd" className="text-xs">New Password (min 8)</Label>
                <button
                  type="button"
                  className={styles.generateBtn}
                  onClick={() => {
                    setResetPasswordVal(generatePassword());
                    setResetReveal(true);
                  }}
                >
                  <Sparkles size={12} />
                  <span>Generate</span>
                </button>
              </div>
              <div className={styles.passwordInputWrap}>
                <input
                  id="reset-agent-pwd"
                  className={styles.passwordInput}
                  type={resetReveal ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={resetPasswordVal}
                  onChange={(e) => setResetPasswordVal(e.target.value)}
                  placeholder="••••••••••••"
                  autoFocus
                />
                <button
                  type="button"
                  className={styles.passwordToggleBtn}
                  onClick={() => setResetReveal(!resetReveal)}
                  title={resetReveal ? 'Hide password' : 'Show password'}
                  aria-label={resetReveal ? 'Hide password' : 'Show password'}
                >
                  {resetReveal ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Setting a new password immediately invalidates active sessions for this agent.
              </p>
            </div>

            {resetError && <p className="text-xs text-destructive font-medium">{resetError}</p>}

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
              <Button type="button" variant="outline" size="sm" onClick={() => setResetAgent(null)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={resetBusy || resetPasswordVal.length < 8}>
                {resetBusy ? 'Updating…' : 'Save New Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// Super-admin edits another agent's profile. Email is editable too; the
// server enforces uniqueness and returns 409 with `error: 'email-taken'`
// which we surface inline. Editing your OWN role is blocked client-side
// — the server would still allow it, but a super-admin demoting themselves
// would lose admin access on the next page load.
function EditAgentModal({
  agent,
  onClose,
  onSaved,
}: {
  agent: Agent;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const me = useAgentStore((s) => s.current);
  const isSelf = me?.agentId === agent.id;
  const [displayName, setDisplayName] = useState(agent.displayName);
  const [email, setEmail] = useState(agent.email);
  const [role, setRole] = useState<Agent['role']>(agent.role);
  const [chatMax, setChatMax] = useState<number>(agent.chatMax);
  const [phone, setPhone] = useState<string>(agent.phone ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(dialogRef, onClose, { canClose: !busy });

  const dirty =
    displayName !== agent.displayName ||
    email !== agent.email ||
    role !== agent.role ||
    chatMax !== agent.chatMax ||
    phone !== (agent.phone ?? '');

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!dirty) return;
    setBusy(true);
    setError(null);
    const patch: Record<string, unknown> = {};
    if (displayName !== agent.displayName) patch['displayName'] = displayName;
    if (email !== agent.email) patch['email'] = email;
    if (role !== agent.role) patch['role'] = role;
    if (chatMax !== agent.chatMax) patch['chatMax'] = chatMax;
    if (phone !== (agent.phone ?? '')) patch['phone'] = phone.trim();
    try {
      await apiFetch(`/api/admin/agents/${agent.id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      await onSaved();
    } catch (err) {
      if (isApiError(err)) {
        const body = err.body as { error?: string } | null;
        if (body?.error === 'email-taken') {
          setError('That email is already in use by another agent.');
        } else if (err.status === 402) {
          setError(planLimitMessage(err));
        } else if (err.status === 400) {
          setError('Invalid input. Check the fields and try again.');
        } else if (err.status === 404) {
          setError('Agent not found — they may have been deleted.');
        } else {
          setError('Update failed. Please try again.');
        }
      } else {
        setError('Update failed. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      ref={dialogRef}
      className={styles.modalBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={`Edit ${agent.displayName}`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form className={styles.modal} onSubmit={save}>
        <div>
          <h2 className={styles.modalTitle}>Edit agent</h2>
          <p className={styles.modalSubtitle}>{agent.id}</p>
        </div>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <div className={styles.modalGrid}>
          <label className={`${styles.field} ${styles.modalGridFull}`}>
            <span className={styles.label}>Display name</span>
            <input
              className={styles.input}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              minLength={1}
            />
          </label>
          <label className={`${styles.field} ${styles.modalGridFull}`}>
            <span className={styles.label}>Email</span>
            <input
              className={styles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Role</span>
            <select
              className={styles.select}
              value={role}
              onChange={(e) => setRole(e.target.value as Agent['role'])}
              disabled={isSelf}
              title={isSelf ? 'You can’t change your own role.' : undefined}
            >
              <RoleOptions platform={me?.role === 'super-admin'} />
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Chat cap</span>
            <input
              className={styles.input}
              type="number"
              min={1}
              max={10}
              value={chatMax}
              onChange={(e) => setChatMax(Number(e.target.value))}
            />
          </label>
          <label className={`${styles.field} ${styles.modalGridFull}`}>
            <span className={styles.label}>WhatsApp phone</span>
            <PhoneInput value={phone} onChange={setPhone} selectClassName={styles.select} inputClassName={styles.input} />
          </label>
        </div>
        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.buttonGhost}
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={styles.button}
            disabled={busy || !dirty || !isValidE164(phone)}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
